import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectPointConstraints,
  selectSegmentConstraints,
} from '../scenarios/scenarioSlice';
import {
  selectAllPoints,
  selectFilteredEdgeNodes,
  selectFilteredEdgePoints,
  selectFilteredEdges,
} from '../map/slice/mapSelectors';
import {
  edgeToGeoJson,
  FeatureLike,
  PropertyExtractor,
} from '../utils/geoJsonUtils';
import { fromLonLat } from 'ol/proj';
import {
  selectAllNodes,
  selectInterconnects,
  selectInterconnectsAtPoints,
} from './slice/mapSelectors';
import {
  Interconnect,
  Node,
  PointConstraint,
  SegmentConstraint,
} from '@/types';
import { selectShowConstraints } from '../plotter/plotterSelectors';
import { useMapLayer } from '../hooks/useMapLayer';
import { useMapContext } from './context/MapContext';

const getEdgeStyle = () => [
  {
    filter: ['==', ['get', 'head'], 0],
    style: {
      'stroke-width': 6,
      'stroke-color': ['get', 'color'],
      'stroke-line-cap': 'round',
      'stroke-line-join': 'round',
    },
  },
  {
    filter: ['==', ['get', 'head'], 1],
    style: {
      'shape-points': 3,
      'shape-radius': 10,
      'shape-fill-color': ['get', 'color'],
      'shape-stroke-color': ['get', 'color'],
      'shape-stroke-width': 4,
      'shape-rotate-with-view': false,
      'shape-displacement': [0, 0],
      'shape-opacity': 1,
      'shape-angle': ['get', 'angle'],
    },
  },
];

const getPointStyle = () => {
  return [
    {
      filter: ['==', ['get', 'interconnect'], 0],
      style: {
        'circle-radius': 4,
        'circle-fill-color': ['get', 'fill'],
        'circle-stroke-color': ['get', 'stroke'],
        'circle-stroke-width': 4,
      },
    },
    {
      filter: [
        'all',
        ['==', ['get', 'interconnect'], 1],
        ['==', ['get', 'head'], 1],
      ],
      style: {
        'shape-points': 3,
        'shape-radius': 10,
        'shape-fill-color': ['get', 'color'],
        'shape-stroke-color': ['get', 'color'],
        'shape-stroke-width': 4,
        'shape-rotate-with-view': false,
        'shape-displacement': [0, 0],
        'shape-opacity': 1,
        'shape-angle': ['get', 'angle'],
      },
    },
    {
      filter: [
        'all',
        ['==', ['get', 'interconnect'], 1],
        ['==', ['get', 'head'], 0],
      ],
      style: {
        'stroke-width': 6,
        'stroke-color': ['get', 'color'],
        'stroke-line-cap': 'round',
        'stroke-line-join': 'round',
      },
    },
  ];
};

const nodeToGEOJson = (
  node: Node,
  constraint: PointConstraint,
): FeatureLike => {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: fromLonLat([Number(node.lng), Number(node.lat)]),
    },
    properties: {
      id: node.id,
      key: `node-${node.id}`,
      stroke: constraint.constraint_factor < 1 ? '#4277d8A0' : '#ff0000A0',
      fill: constraint.constraint_factor < 1 ? '#4277d820' : '#ff000020',
      interconnect: 0,
      head: 0,
      angle: 0,
    },
  };
};

const extractConstraintGeoJsonProps = (
  { constraint }: { constraint: SegmentConstraint },
  _arrowHead: boolean,
) => {
  return {
    key: `edge-${constraint.edge}`,
    id: constraint.edge,
    color: constraint.constraint_factor >= 1 ? '#ff0000A0' : '#4277d8A0',
  };
};

const extractInterconnectConstraintGeoJsonProps: PropertyExtractor<{
  positions: [number, number][];
  interconnect: Interconnect;
  constraint: PointConstraint;
}> = ({ constraint, interconnect }) => {
  return {
    key: `interconnect-${interconnect.id}`,
    id: interconnect.id,
    color: constraint.constraint_factor >= 1 ? '#ff0000A0' : '#4277d8A0',
    interconnect: 1,
  };
};

export type MapConstraintsProps = {
  zIndex: number;
};

export const MapMajorSegmentConstraints: React.FC<MapConstraintsProps> = ({
  zIndex,
}) => {
  const segmentConstraints = useSelector(selectSegmentConstraints);
  const edges = useSelector(selectFilteredEdges);
  const showConstraints = useSelector(selectShowConstraints);

  const features = useMemo(
    () =>
      showConstraints
        ? Object.values(segmentConstraints)
            .filter((constraint) => constraint.constraint_factor >= 1)
            .flatMap((constraint) => {
              const edge = edges[constraint.edge];
              const positions =
                constraint.flow_direction === 'FORWARDHAUL'
                  ? edge?.positions
                  : edge?.positions?.slice().reverse();
              if (!positions) return [];
              return edgeToGeoJson(
                { positions, edge, constraint },
                extractConstraintGeoJsonProps,
                true,
              );
            })
        : [],
    [segmentConstraints, edges, showConstraints],
  );

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(features, { zIndex, style });

  return <></>;
};

export type MapMinorSegmentConstraintsProps = {
  zIndex: number;
};

export const MapMinorSegmentConstraints: React.FC<
  MapMinorSegmentConstraintsProps
> = ({ zIndex }) => {
  const { map } = useMapContext();
  const segmentConstraints = useSelector(selectSegmentConstraints);
  const edges = useSelector(selectFilteredEdges);
  const showConstraints = useSelector(selectShowConstraints);

  const [showDetailedConstraints, setShowDetailedConstraints] = useState(
    map?.getView().getZoom() ?? 0 > 8,
  );

  const features = useMemo(
    () =>
      showConstraints && showDetailedConstraints
        ? Object.values(segmentConstraints)
            .filter((constraint) => constraint.constraint_factor < 1)
            .flatMap((constraint) => {
              const edge = edges[constraint.edge];
              const positions =
                constraint.flow_direction === 'FORWARDHAUL'
                  ? edge?.positions
                  : edge?.positions?.slice().reverse();
              if (!positions) return [];
              return edgeToGeoJson(
                { positions, edge, constraint },
                extractConstraintGeoJsonProps,
                true,
              );
            })
        : [],
    [segmentConstraints, edges, showConstraints, showDetailedConstraints],
  );

  const style = useMemo(() => getEdgeStyle(), []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    if (!map) return;
    const onChangeZoom = () =>
      setShowDetailedConstraints((map.getView().getZoom() ?? -1) > 8);
    map.getView().on('change:resolution', onChangeZoom);
    return () => {
      map.getView().un('change:resolution', onChangeZoom);
    };
  }, [map]);

  return <></>;
};

export type MapMajorPointConstraintsProps = {
  zIndex: number;
};

export const MapMajorPointConstraints: React.FC<
  MapMajorPointConstraintsProps
> = ({ zIndex }) => {
  const pointConstraints = useSelector(selectPointConstraints);
  const nodes = useSelector(selectFilteredEdgeNodes);
  const allNodes = useSelector(selectAllNodes);
  const points = useSelector(selectFilteredEdgePoints);
  const allPoints = useSelector(selectAllPoints);
  const interconnectsAtPoints = useSelector(selectInterconnectsAtPoints);
  const showConstraints = useSelector(selectShowConstraints);

  const features = useMemo(
    () =>
      showConstraints
        ? Object.values(pointConstraints)
            .filter((constraint) => constraint.constraint_factor >= 1)
            .flatMap((constraint) => {
              const point = points[constraint.point];
              const node = nodes[point?.node ?? ''];
              if (!node || !point) return [];
              const interconnectsAtPoint =
                interconnectsAtPoints[point.id] ?? [];
              if (interconnectsAtPoint.length > 0) {
                return interconnectsAtPoint.flatMap((ic) => {
                  const startPoint = allPoints[ic.start_point];
                  const startNode = allNodes[startPoint?.node ?? ''];
                  const endPoint = allPoints[ic.end_point];
                  const endNode = allNodes[endPoint?.node ?? ''];

                  if (!startNode || !endNode) return [];
                  const positions: [number, number][] =
                    (constraint.flow_direction === 'DELIVERY' &&
                      ic.start_point === point.id) ||
                    (constraint.flow_direction === 'RECEIPT' &&
                      ic.end_point === point.id)
                      ? [
                          [Number(startNode.lat), Number(startNode.lng)],
                          [Number(endNode.lat), Number(endNode.lng)],
                        ]
                      : [
                          [Number(endNode.lat), Number(endNode.lng)],
                          [Number(startNode.lat), Number(startNode.lng)],
                        ];

                  return edgeToGeoJson(
                    {
                      positions,
                      interconnect: ic,
                      constraint,
                    },
                    extractInterconnectConstraintGeoJsonProps,
                    true,
                  );
                });
              } else {
                return [nodeToGEOJson(node, constraint)];
              }
            })
        : [],
    [
      pointConstraints,
      nodes,
      showConstraints,
      interconnectsAtPoints,
      allPoints,
      allNodes,
    ],
  );

  const style = useMemo(() => getPointStyle(), []);

  useMapLayer(features, { zIndex, style });

  return <></>;
};

export type MapMinorPointConstraintsProps = {
  zIndex: number;
};

export const MapMinorPointConstraints: React.FC<
  MapMinorPointConstraintsProps
> = ({ zIndex }) => {
  const { map } = useMapContext();
  const pointConstraints = useSelector(selectPointConstraints);
  const nodes = useSelector(selectFilteredEdgeNodes);
  const allNodes = useSelector(selectAllNodes);
  const points = useSelector(selectFilteredEdgePoints);
  const allPoints = useSelector(selectAllPoints);
  const interconnects = useSelector(selectInterconnects);
  const showConstraints = useSelector(selectShowConstraints);

  const [showDetailedConstraints, setShowDetailedConstraints] = useState(
    map?.getView().getZoom() ?? 0 > 8,
  );

  const features = useMemo(
    () =>
      showConstraints && showDetailedConstraints
        ? Object.values(pointConstraints)
            .filter((constraint) => constraint.constraint_factor < 1)
            .flatMap((constraint) => {
              const point = points[constraint.point];
              const node = nodes[point?.node ?? ''];
              if (!node || !point) return [];
              const interconnectsAtPoint = Object.values(interconnects).filter(
                (ic) => {
                  return (
                    Number(ic.end_point) === Number(point.id) ||
                    Number(ic.start_point) === Number(point.id)
                  );
                },
              );
              if (interconnectsAtPoint.length > 0) {
                return interconnectsAtPoint.flatMap((ic) => {
                  const startPoint = allPoints[ic.start_point];
                  const startNode = allNodes[startPoint?.node ?? ''];
                  const endPoint = allPoints[ic.end_point];
                  const endNode = allNodes[endPoint?.node ?? ''];

                  if (!startNode || !endNode) return [];
                  const positions: [number, number][] =
                    (constraint.flow_direction === 'DELIVERY' &&
                      ic.start_point === point.id) ||
                    (constraint.flow_direction === 'RECEIPT' &&
                      ic.end_point === point.id)
                      ? [
                          [Number(startNode.lat), Number(startNode.lng)],
                          [Number(endNode.lat), Number(endNode.lng)],
                        ]
                      : [
                          [Number(endNode.lat), Number(endNode.lng)],
                          [Number(startNode.lat), Number(startNode.lng)],
                        ];

                  return edgeToGeoJson(
                    { positions, interconnect: ic, constraint },
                    extractInterconnectConstraintGeoJsonProps,
                    true,
                  );
                });
              } else {
                return [nodeToGEOJson(node, constraint)];
              }
            })
        : [],
    [
      pointConstraints,
      nodes,
      showConstraints,
      showDetailedConstraints,
      interconnects,
      allPoints,
      allNodes,
    ],
  );

  const style = useMemo(() => getPointStyle(), []);

  useMapLayer(features, { zIndex, style });

  useEffect(() => {
    if (!map) return;
    const onChangeZoom = () => {
      setShowDetailedConstraints((map.getView().getZoom() ?? -1) > 8);
    };
    map.getView().on('change:resolution', onChangeZoom);
    return () => {
      map.getView().un('change:resolution', onChangeZoom);
    };
  }, [map]);

  return null;
};
